import { type AgeingAnalysisFilters, type CustomerAgeingData } from "@/types/ageing-analysis";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { MultiSelect } from "@/components/ui/multiselect";

interface AdvancedFiltersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: AgeingAnalysisFilters;
  onFiltersChange: (filters: AgeingAnalysisFilters) => void;
  onApply: () => void;
  onReset: () => void;
  customers: CustomerAgeingData[];
}

export function AdvancedFiltersSheet({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  customers,
}: Readonly<AdvancedFiltersSheetProps>) {
  const updateFilter = <K extends keyof AgeingAnalysisFilters>(key: K, value: AgeingAnalysisFilters[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  // Create customer options for multi-select
  const customerOptions = customers.map(customer => ({
    value: customer.id,
    label: `[${customer.accountNumber}] ${customer.customerName}`
  }));

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md pl-8">
        <SheetHeader>
          <SheetTitle>Filters</SheetTitle>
          <SheetDescription>
            Refine your customer list with advanced filtering options
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4 mt-6">
          <div className="space-y-6">
            {/* Customer */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Customer</Label>
              <MultiSelect
                options={customerOptions}
                placeholder="Select customers..."
                emptyMessage="No customers found."
                value={filters.customerIds}
                onValueChange={(value) => updateFilter("customerIds", value)}
                maxDisplay={2}
              />
            </div>

            <Separator />

            {/* Risk Level */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Risk Level</Label>
              <MultiSelect
                options={[
                  { value: 'critical', label: 'Critical' },
                  { value: 'high', label: 'High' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'low', label: 'Low' }
                ]}
                placeholder="Select risk levels..."
                emptyMessage="No risk levels found."
                value={filters.riskLevel}
                onValueChange={(value) => updateFilter("riskLevel", value)}
                maxDisplay={2}
              />
            </div>

            <Separator />

            {/* Collection Strategy */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Collection Strategy</Label>
              <MultiSelect
                options={[
                  { value: 'Standard Collection Flow', label: 'Standard Collection Flow' },
                  { value: 'Aggressive Collection', label: 'Aggressive Collection' },
                  { value: 'Gentle Reminder Flow', label: 'Gentle Reminder Flow' },
                  { value: 'High-Value Customer', label: 'High-Value Customer' },
                  { value: 'Legal Action Track', label: 'Legal Action Track' }
                ]}
                placeholder="Select collection strategies..."
                emptyMessage="No collection strategies found."
                value={filters.collectionStrategy}
                onValueChange={(value) => updateFilter("collectionStrategy", value)}
                maxDisplay={2}
              />
            </div>

            <Separator />

            {/* Has Open Disputes */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Has Open Disputes</Label>
              <Select
                value={
                  filters.hasDisputes === null
                    ? "all"
                    : filters.hasDisputes
                    ? "yes"
                    : "no"
                }
                onValueChange={(value) => {
                  updateFilter(
                    "hasDisputes",
                    value === "all" ? null : value === "yes"
                  );
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Has Promises to Pay */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Has Promises to Pay</Label>
              <Select
                value={
                  filters.hasPromisesToPay === null
                    ? "all"
                    : filters.hasPromisesToPay
                    ? "yes"
                    : "no"
                }
                onValueChange={(value) => {
                  updateFilter(
                    "hasPromisesToPay",
                    value === "all" ? null : value === "yes"
                  );
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Days Overdue Range */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Days Overdue Range</Label>
              <div className="space-y-4">
                <div className="px-2">
                  <Slider
                    min={0}
                    max={180}
                    step={10}
                    value={[
                      filters.daysOverdueMin ?? 0,
                      filters.daysOverdueMax ?? 180
                    ]}
                    onValueChange={([min, max]) => {
                      updateFilter("daysOverdueMin", min);
                      updateFilter("daysOverdueMax", max);
                    }}
                    className="w-full"
                  />
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{filters.daysOverdueMin ?? 0} days</span>
                  <span>{filters.daysOverdueMax ?? 180} days</span>
                </div>
              </div>
            </div>

            <Separator />

            {/* Outstanding Amount Range */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Outstanding Amount Range</Label>
              <div className="space-y-4">
                <div className="px-2">
                  <Slider
                    min={0}
                    max={500000}
                    step={10000}
                    value={[
                      filters.outstandingMin ?? 0,
                      filters.outstandingMax ?? 500000
                    ]}
                    onValueChange={([min, max]) => {
                      updateFilter("outstandingMin", min);
                      updateFilter("outstandingMax", max);
                    }}
                    className="w-full"
                  />
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{formatCurrency(filters.outstandingMin ?? 0)}</span>
                  <span>{formatCurrency(filters.outstandingMax ?? 500000)}</span>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={onReset}>
            Reset
          </Button>
          <Button onClick={onApply}>Apply Filters</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
